import { Component, computed, effect, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { Impediment, ImpedimentGroup } from '../../../api/app-types/impediment.app';
import { IMPEDIMENT_PROVIDER } from '../../../api/services/impediment.service';
import { LvrClientReport } from '../../../core/components/client-report/client-report';
import { LvrDefaultActions } from '../../../core/components/default-actions/default.actions';
import { LvrHeading } from '../../../core/components/heading/heading';
import { LvrHtmlEditor } from '../../../core/components/html-editor/html-editor';
import { mergeOriginalTemplateWithEditedContent } from '../../../core/components/html-editor/template.func';
import { ImpedimentList } from '../../../core/components/impediment-list/impediment-list';
import { LvrSection } from '../../../core/components/section/section';
import { PaginatedResponse } from '../../../shared/pagination/paginated-response';
import { validateIfIndicatorsAreSet } from '../../../shared/validators/editor-report-indicators.validator';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { createErrorNotification, createSuccessNotification } from '../../../shared/notification/notification.factory';

@Component({
	selector: 'lvr-start-report',
	templateUrl: './start-report.html',
	styleUrl: './start-report.scss',
	imports: [
		TranslocoPipe,
		LvrHeading,
		LvrHtmlEditor,
		MatTooltip,
		ReactiveFormsModule,
		ImpedimentList,
		LvrDefaultActions,
		LvrSection,
		LvrButton,
	],
})
export class LvrStartReport extends LvrClientReport {
	private readonly fb = inject(FormBuilder);
	private readonly impedimentApi = inject(IMPEDIMENT_PROVIDER);
	public readonly availableImpediments = this.resolveDataAsSignal<ImpedimentGroup[]>('impedimentGroups');
	private readonly impedimentsOnReportPage =
		this.resolveDataAsSignal<PaginatedResponse<Impediment>>('impedimentsOnReportPage');
	public readonly isUsbAssignment = computed(() => this.assignment()?.typeOfCare?.initial === 'ÜSB');

	public readonly form = this.fb.group({
		impediments: this.fb.control<number[] | null>(null),
	});

	private readonly START_REPORT_SAVE_LOADING_KEY = `start-report-${this.assignmentId()}.save`;
	public readonly isLoadingForSaveStartReport = this.loading.isCurrentlyLoading(
		this.START_REPORT_SAVE_LOADING_KEY,
		'requestKey',
	);

	public constructor() {
		super();
		effect(() => {
			if (!this.impedimentsOnReportPage() || this.impedimentsOnReportPage()!.entities.length < 1) return;
			const impedimentIds = this.impedimentsOnReportPage()!.entities.map((i) => i.id);
			this.form.get('impediments')!.patchValue(impedimentIds);
		});
	}

	public async save() {
		if (!this.canEditReport()) return;

		const report = this.report()!;
		const template = report.template;

		if (!template) {
			// no template -> no save
			return;
		}

		const content = mergeOriginalTemplateWithEditedContent(template.htmlContent, this.updateHtmlContent());

		const impedimentIds = this.form.value.impediments || [];
		const hasIndicators = validateIfIndicatorsAreSet(content);
		const hasValidImpediments = this.form.controls.impediments.valid;

		const uuid = this.loading.setLoadingState(window.crypto.randomUUID(), this.START_REPORT_SAVE_LOADING_KEY);
		try {
			await this.impedimentApi.saveImpedimentsToReport(report.id, impedimentIds);
			await this.api.updateReportDocument({
				assignmentId: this.assignmentId()!,
				htmlContent: content,
				reportId: report.id,
				isFinal: false,
				isDraft: !(hasIndicators && hasValidImpediments),
				reportDocumentTemplateId: template.id,
			});

			await this.navigateAfterFinished(this.getAssignmentDetailsCommands('../..'), {
				relativeTo: this.route,
			});

			this.ntfy.addNotification(
				createSuccessNotification(this.transloco.translate('base.notification_message.save_report_success')),
			);
		} catch {
			this.ntfy.addNotification(
				createErrorNotification(this.transloco.translate('base.notification_message.save_report_error')),
			);
		} finally {
			this.loading.clearLoadingState(uuid);
		}
	}
}
